import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckCircle } from "@coreui/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import type { ApiError } from "../../../api/client";
import { useFailure } from "../../../context/useFailure";
import { useCartStore, type UpgradeLine } from "../../../state/cartStore";
import {
  MY_SUBSCRIPTIONS_QUERY_KEY,
  useMySubscriptionsAwaitingUpgrade,
  useUpgradeToYearly,
  type UpgradeSessionResponse,
} from "../../../api/hooks/useStrategySubscriptions";
import { BILLING_QUERY_KEY } from "../../../api/hooks/useBilling";
import {
  usableOffers,
  useUsableOffers,
  type UsableOfferDto,
} from "../../../api/hooks/useOffers";
import { savingOnLine } from "../../../lib/offer-savings";
import { formatPriceUsd } from "../../../lib/money";
import { aYearOn, formatDateShort, formatDayMonth } from "../../../lib/dates";
import { stripePromise } from "../../../lib/stripe";
import { routePaths } from "../../../routes/route-paths";
import { useTradingStrategies } from "../../../api/hooks/useTradingStrategies";
import { StrategySideLabel } from "../components/StrategySideLabel";

// CStepper's imperative API, as the purchase checkout uses it.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

// How long Done waits for the upgrade to show as booked before pointing at support. The card is saved at
// once, but the message processor books the upgrade a few hops later.
const BOOKING_WAIT_MS = 90_000;

interface UpgradeFigures {
  line: UpgradeLine;
  offerName: string | null;
  firstYearUsd: number;
  laterYearsUsd: number;
}

function figuresFor(line: UpgradeLine, offers: UsableOfferDto[]): UpgradeFigures {
  const offer = offers.find((o) => o.id === line.offerId) ?? null;
  const saving = offer ? savingOnLine(offer, line.yearlyPriceUsd) : 0;
  const firstYearUsd = line.yearlyPriceUsd - saving;
  // An offer with no end runs every year; one with an end is spent on the first.
  const offerRunsEveryYear = offer !== null && offer.durationMonths === null;

  return {
    line,
    offerName: offer?.publicName ?? null,
    firstYearUsd,
    laterYearsUsd: offerRunsEveryYear ? firstYearUsd : line.yearlyPriceUsd,
  };
}

/** The checkout for an upgrade to yearly: its own review and confirmation around the usual card form. */
export function UpgradeCheckoutWizard() {
  const navigate = useNavigate();
  const cartUpgrade = useCartStore((s) => s.upgrade);
  const clearCart = useCartStore((s) => s.clear);
  const setCheckoutLocked = useCartStore((s) => s.setCheckoutLocked);
  const offers = usableOffers(useUsableOffers().data);
  const { data: strategies } = useTradingStrategies();
  const openUpgradeSession = useUpgradeToYearly();
  const queryClient = useQueryClient();
  const { showFailure, clearFailure } = useFailure();
  const stepperRef = useRef<CStepperRef>(null);

  // Kept from when the card session opens: paying empties the cart, and Done still has to say what was
  // booked.
  const [booking, setBooking] = useState<UpgradeFigures | null>(null);
  const [session, setSession] = useState<UpgradeSessionResponse | null>(null);
  const [openingSession, setOpeningSession] = useState(false);
  const [paid, setPaid] = useState(false);
  const [bookingTimedOut, setBookingTimedOut] = useState(false);

  const upgrade = booking ?? (cartUpgrade ? figuresFor(cartUpgrade, offers) : null);
  const strategyId = upgrade?.line.strategyId ?? null;
  const awaitingBooking = paid && !bookingTimedOut;
  const subscriptions = useMySubscriptionsAwaitingUpgrade(
    awaitingBooking ? strategyId : null
  );

  // Only the live row renews, so it is the one being upgraded even where ended rows share the strategy.
  const subscription = subscriptions.data?.find(
    (s) => s.strategy.id === strategyId && s.renewsOn !== null
  );
  const pendingSwitch =
    subscriptions.data?.find((s) => s.strategy.id === strategyId && s.pendingSwitch !== null)
      ?.pendingSwitch ?? null;
  const booked = paid && pendingSwitch !== null;

  // Stripe's date and figure once the session is open; the cart's until then.
  const switchDate = session?.switchDate ?? subscription?.renewsOn ?? null;
  const firstChargeUsd = session?.firstYearlyChargeUsd ?? upgrade?.firstYearUsd ?? 0;
  const checkoutStarted = session !== null;
  const notRenewing =
    !checkoutStarted && subscriptions.data !== undefined && switchDate === null;

  // Freeze the cart for exactly as long as a session is open, as the purchase checkout does.
  useEffect(() => {
    setCheckoutLocked(checkoutStarted);
    return () => setCheckoutLocked(false);
  }, [checkoutStarted, setCheckoutLocked]);

  useEffect(() => {
    if (!paid || booked || bookingTimedOut) {
      return;
    }

    const timer = setTimeout(() => setBookingTimedOut(true), BOOKING_WAIT_MS);
    return () => clearTimeout(timer);
  }, [paid, booked, bookingTimedOut]);

  const handleProceedToPayment = async () => {
    if (upgrade === null) {
      return;
    }

    setOpeningSession(true);
    clearFailure();
    try {
      const opened = await openUpgradeSession.mutateAsync({
        strategyId: upgrade.line.strategyId,
        offerId: upgrade.line.offerId,
      });
      setBooking(upgrade);
      setSession(opened);
      stepperRef.current?.next();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "We couldn't start your upgrade. Please try again.";
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setOpeningSession(false);
    }
  };

  // Leaving Payment abandons the session, which unlocks the cart. Returning opens a fresh one.
  const handleBackFromPayment = () => {
    setSession(null);
    setBooking(null);
    stepperRef.current?.prev();
  };

  const handlePaymentComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
    // The yearly subscription exists in Stripe from now, so the next payment on Billing changes.
    queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    setPaid(true);
    clearCart();
    stepperRef.current?.next();
  }, [clearCart, queryClient, setPaid]);

  const embeddedOptions = useMemo(
    () =>
      session
        ? { clientSecret: session.clientSecret, onComplete: handlePaymentComplete }
        : null,
    [session, handlePaymentComplete]
  );

  // Below every hook, as in the purchase checkout: returning earlier changes the hook count.
  if (upgrade === null) {
    return (
      <div>
        <CAlert color="info">
          Your cart is empty.{" "}
          <Link to={routePaths.strategies}>Browse strategies</Link>.
        </CAlert>
      </div>
    );
  }

  const name = upgrade.line.name;
  const direction = strategies?.find((s) => s.id === upgrade.line.strategyId)?.direction;
  const yearStart = pendingSwitch?.switchDate ?? switchDate;

  const steps = [
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review your upgrade</h4>
          </CCardHeader>
          <CCardBody>
            <p className="mb-3">
              <strong>{name}</strong>
              <StrategySideLabel direction={direction} className="small ms-2 align-middle" />
              {upgrade.offerName && (
                <span className="text-body-secondary"> · {upgrade.offerName}</span>
              )}
            </p>

            {notRenewing && (
              <CAlert color="warning">
                {name} isn't renewing any more, so it can't move to yearly.
              </CAlert>
            )}

            {switchDate && (
              <>
                <div className="border rounded px-3">
                  <div className="d-flex justify-content-between gap-3 py-2 border-bottom fw-bold">
                    <span>Due today</span>
                    <span className="text-nowrap">{formatPriceUsd(0)}</span>
                  </div>
                  <div className="d-flex justify-content-between gap-3 py-2 border-bottom">
                    <span>
                      {formatDateShort(switchDate)}
                      <span className="d-block text-body-secondary small">
                        A year, to {formatDateShort(aYearOn(switchDate))}
                      </span>
                    </span>
                    <span className="text-nowrap">{formatPriceUsd(upgrade.firstYearUsd)}</span>
                  </div>
                  <div className="d-flex justify-content-between gap-3 py-2">
                    <span>Every {formatDayMonth(switchDate)} after</span>
                    <span className="text-nowrap">{formatPriceUsd(upgrade.laterYearsUsd)}</span>
                  </div>
                </div>

                <p className="text-body-secondary small mt-3 mb-0">
                  Nothing is taken today. On {formatDateShort(switchDate)},{" "}
                  {formatPriceUsd(upgrade.firstYearUsd)} is taken for a year of {name},
                  to {formatDateShort(aYearOn(switchDate))}. It then renews at{" "}
                  {formatPriceUsd(upgrade.laterYearsUsd)} every {formatDayMonth(switchDate)}{" "}
                  until you end it. Until {formatDateShort(switchDate)} you can stay monthly
                  from {name}'s page. After that you can end it from My strategies: a
                  year isn't refunded part-way, and access runs to the end of it. Your
                  access doesn't change.
                </p>
              </>
            )}

            <div className="d-flex justify-content-between mt-4">
              <CButton
                color="secondary"
                variant="outline"
                onClick={() => navigate(routePaths.cart)}
              >
                Back to cart
              </CButton>
              <CButton
                color="primary"
                onClick={handleProceedToPayment}
                disabled={switchDate === null || openingSession}
              >
                {openingSession && <CSpinner size="sm" className="me-2" />}
                Next: Payment
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Payment",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Payment</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              Due today: <strong>{formatPriceUsd(0)}</strong>.
              {session?.firstYearlyChargeUsd != null && (
                <>
                  {" "}
                  Stripe takes {formatPriceUsd(session.firstYearlyChargeUsd)} on{" "}
                  {formatDateShort(session.switchDate)}, when your year starts.
                </>
              )}
            </p>
            {session?.endsMonthlyDiscount && (
              <p className="text-body-secondary">
                The discount on {name}'s monthly price ends on{" "}
                {formatDateShort(session.switchDate)}, with the monthly bill.
                {session.monthlyDiscountEnd && (
                  <>
                    {" "}
                    It would otherwise have run to{" "}
                    {formatDateShort(session.monthlyDiscountEnd)}.
                  </>
                )}
              </p>
            )}
            <p className="text-body-secondary">
              Your cart is locked while this payment is open. Use{" "}
              <strong>Back</strong> if you need to change it.
            </p>
            {embeddedOptions ? (
              <div className="mb-3">
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={embeddedOptions}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            ) : (
              <div className="text-center py-5 text-body-secondary">
                <CSpinner size="sm" className="me-2" />
                Preparing secure checkout…
              </div>
            )}
            <div className="d-flex justify-content-start">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBackFromPayment}
              >
                Back
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Done",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{booked ? "All set" : "Confirming your upgrade"}</h4>
          </CCardHeader>
          <CCardBody>
            {booked ? (
              <div className="text-center py-3 mb-2">
                <CIcon
                  icon={cilCheckCircle}
                  size="3xl"
                  className="text-success mb-3"
                />
                <h3 className="mb-2">Your upgrade is confirmed</h3>
                {yearStart && (
                  <p
                    className="text-body-secondary mx-auto"
                    style={{ maxWidth: 480 }}
                  >
                    {name} moves to yearly on {formatDateShort(yearStart)}.{" "}
                    {formatPriceUsd(firstChargeUsd)} is taken then, and{" "}
                    {formatPriceUsd(upgrade.laterYearsUsd)} every{" "}
                    {formatDayMonth(yearStart)} after. Until then you can stay
                    monthly from its page. Your access doesn't change.
                  </p>
                )}
              </div>
            ) : bookingTimedOut ? (
              <div className="tinted-warning rounded p-3 mb-3">
                Your card is saved, but the upgrade hasn't shown up yet. Look at{" "}
                {name}'s page again in a few minutes, and contact support if it
                isn't there by tomorrow.
              </div>
            ) : (
              <div className="text-center py-4 text-body-secondary">
                <CSpinner size="sm" className="me-2" />
                Your card is saved. Confirming your upgrade, which usually takes a
                few seconds.
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 justify-content-center">
              <Link
                to={`/subscriptions/${upgrade.line.strategyId}`}
                className="btn btn-primary"
              >
                View {name}
              </Link>
              <Link
                to={routePaths.myStrategies}
                className="btn btn-outline-secondary"
              >
                My strategies
              </Link>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>Checkout</h2>
      <p className="text-body-secondary">
        Review your upgrade and save your card. Nothing is taken until your year
        starts.
      </p>
      <hr />

      <CStepper ref={stepperRef} steps={steps} />
    </>
  );
}
